export const ACTION_ART_SIDES = ['w', 'b'] as const;
export const ACTION_ART_ROLES = ['p', 'n', 'b', 'r', 'q', 'k'] as const;

export type ActionArtSide = typeof ACTION_ART_SIDES[number];
export type ActionArtRole = typeof ACTION_ART_ROLES[number];
export type ActionArtKey = `${ActionArtSide}${ActionArtRole}`;

export const ACTION_ART_KEYS = Object.freeze(ACTION_ART_SIDES.flatMap((side) => (
  ACTION_ART_ROLES.map((role) => `${side}${role}` as const)
))) as readonly ActionArtKey[];

export type ActionStripFrameIndex = 0 | 1 | 2 | 3 | 4 | 5;

export interface ActionStripFrameTiming {
  readonly frame: ActionStripFrameIndex;
  readonly startsAtMs: number;
  readonly durationMs: number;
}

export type ActionStripFrameSchedule = readonly [
  ActionStripFrameTiming,
  ActionStripFrameTiming,
  ActionStripFrameTiming,
  ActionStripFrameTiming,
  ActionStripFrameTiming,
  ActionStripFrameTiming,
];

export interface ActionStripSource {
  readonly url: string;
  readonly revision: string;
  readonly frameSchedule: ActionStripFrameSchedule;
}

export type ActionStripSources = Readonly<Record<ActionArtKey, ActionStripSource>>;

export interface ActionStripAsset extends ActionStripSource {
  readonly key: ActionArtKey;
  readonly side: ActionArtSide;
  readonly role: ActionArtRole;
  readonly mimeType: 'image/webp';
  readonly lossless: true;
  readonly width: 512;
  readonly height: 3072;
  readonly frameWidth: 512;
  readonly frameHeight: 512;
  readonly frameCount: 6;
}

export type ActionStripRegistry = Readonly<Record<ActionArtKey, ActionStripAsset>>;

export function actionArtKey(side: ActionArtSide, role: ActionArtRole): ActionArtKey {
  return `${side}${role}`;
}

function freezeSchedule(schedule: ActionStripFrameSchedule): ActionStripFrameSchedule {
  if (schedule.length !== 6) {
    throw new Error('Action strips require exactly six frame timings.');
  }

  return Object.freeze(schedule.map((timing, frame) => {
    if (
      timing.frame !== frame
      || !Number.isFinite(timing.startsAtMs)
      || timing.startsAtMs < 0
      || !Number.isFinite(timing.durationMs)
      || timing.durationMs <= 0
    ) {
      throw new Error(`Invalid action strip frame timing at frame ${frame}.`);
    }
    return Object.freeze({ ...timing });
  })) as unknown as ActionStripFrameSchedule;
}

function validateRevisionQualifiedUrl(key: ActionArtKey, source: ActionStripSource): void {
  if (!/^[0-9a-f]{12,}$/.test(source.revision)) {
    throw new Error(`Action strip ${key} revision must be at least twelve lowercase hexadecimal characters.`);
  }

  let url: URL;
  try {
    url = new URL(source.url, 'https://capychess.invalid');
  } catch {
    throw new Error(`Action strip ${key} has an invalid revision-qualified URL.`);
  }
  const tokens = url.searchParams.getAll('v');
  if (
    tokens.length !== 1
    || !/^[0-9a-f]{12}$/.test(tokens[0] ?? '')
    || tokens[0] !== source.revision.slice(0, 12)
  ) {
    throw new Error(
      `Action strip ${key} URL revision must be one twelve-character lowercase hex token matching the revision prefix.`,
    );
  }
}

/**
 * Builds the exhaustive twelve-strip registry from release-owned source data.
 * URLs, revision tokens, and schedules stay injected until approved assets exist.
 */
export function createActionStripRegistry(sources: ActionStripSources): ActionStripRegistry {
  const entries = ACTION_ART_KEYS.map((key) => {
    const source = sources[key];
    if (source === undefined || source.url.length === 0 || source.revision.length === 0) {
      throw new Error(`Missing action strip source metadata for ${key}.`);
    }
    validateRevisionQualifiedUrl(key, source);

    const asset: ActionStripAsset = Object.freeze({
      key,
      side: key[0] as ActionArtSide,
      role: key[1] as ActionArtRole,
      url: source.url,
      revision: source.revision,
      mimeType: 'image/webp',
      lossless: true,
      width: 512,
      height: 3072,
      frameWidth: 512,
      frameHeight: 512,
      frameCount: 6,
      frameSchedule: freezeSchedule(source.frameSchedule),
    });
    return [key, asset] as const;
  });

  return Object.freeze(Object.fromEntries(entries)) as ActionStripRegistry;
}
